import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Conversation } from "@elevenlabs/client";
import { Mic, MicOff, Loader2, MonitorSmartphone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const HEARTBEAT_MS = 30000;
const PREF_KEY = "grafton_voice_pref";

const PAGES: Record<string, string> = {
  accueil: "/dashboard",
  mon_business: "/mon-business",
  clients_portage: "/portage-clients",
  tresorerie: "/tresorerie",
  cfo: "/tresorerie",
  crm: "/crm",
  ventes: "/ventes",
  social: "/social",
  briefing: "/briefing",
  comptabilite: "/comptabilite",
  financement: "/financement",
  agenda: "/agenda",
  emails: "/emails",
  marketing: "/marketing",
  nanocorp: "/nanocorp",
  lead_hunters: "/lead-hunters",
  closers: "/closers",
  parametres: "/parametres",
  voix: "/voix",
  mon_plan: "/mon-plan",
  pilotage: "/pilotage",
  savoir_faire: "/savoir-faire",
  laboratoire: "/laboratoire",
  espace_physique: "/laboratoire/espace-physique",
  mon_site: "/my-website",
  atelier: "/atelier",
  communication: "/communication",
  tunnel_vente: "/tunnel-vente",
  business_plan: "/business-plan",
  marches_publics: "/marches-publics",
  visiteurs_en_direct: "/visiteurs-live",
  equipe: "/equipe",
  lever_des_fonds: "/lever-des-fonds",
  rh: "/rh",
  appels: "/appels",
  administratif: "/administratif",
  partenaires: "/partenaires",
  creation_entreprise: "/creation-entreprise",
  grafton_autonome: "/grafton-autonome",
  connexions_api: "/connexions-api",
  competences: "/competences",
  adn_grafton: "/adn-grafton",
  parrainage: "/parrainage",
  paiement: "/paiement",
  securite: "/securite",
  offres: "/offres",
};

// Pages où la voix globale ne doit pas se lancer (publiques, connexion, page voix dédiée)
const EXCLUDED = ["/", "/auth", "/voix", "/tarifs", "/contact", "/vision", "/crowdfunding", "/seminaire", "/ateliers", "/recrutement", "/diagnostic", "/bourse-idees", "/offres", "/modeles"];

type Status = "off" | "idle" | "starting" | "live" | "locked" | "error" | "denied";
type Mode = "listening" | "speaking";

function getDeviceId(): string {
  try {
    let id = localStorage.getItem("grafton_device_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("grafton_device_id", id);
    }
    return id;
  } catch {
    return `anon-${Math.random().toString(36).slice(2)}`;
  }
}

function readPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

export default function GlobalVoice() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<Status>("idle");
  const [mode, setMode] = useState<Mode>("listening");
  const [level, setLevel] = useState(0);
  const [deaf, setDeaf] = useState(false);
  const deviceId = useRef(getDeviceId());
  const convRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null);
  const timer = useRef<number | null>(null);
  const busy = useRef(false);

  const excluded = EXCLUDED.includes(pathname);
  const enabled = !!user && !excluded && !pathname.startsWith("/modeles") && !pathname.startsWith("/s/") && pathname !== "/atelier";

  const post = useCallback(
    (path: string, body: Record<string, unknown> = {}) =>
      fetch(`${API_URL}/api/voice/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId.current, ...body }),
        signal: AbortSignal.timeout(55000), // jamais d'attente infinie : au-delà, on propose le chat
      }),
    []
  );

  const clearTimer = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };

  const stop = useCallback(
    async (remember: boolean) => {
      clearTimer();
      const conv = convRef.current;
      convRef.current = null;
      if (remember) {
        try { localStorage.setItem(PREF_KEY, "off"); } catch { /* ignoré */ }
      }
      setStatus(remember ? "off" : "idle");
      try { await conv?.endSession(); } catch { /* déjà fermée */ }
      try { await post("release"); } catch { /* le verrou expire seul */ }
    },
    [post]
  );

  const start = useCallback(
    async (takeover = false) => {
      if (busy.current || convRef.current) return;
      busy.current = true;
      setStatus("starting");
      try { localStorage.removeItem(PREF_KEY); } catch { /* ignoré */ }
      try {
        let res = await post("session", { takeover });
        if (res.status === 401) {
          // session expirée : on la renouvelle en silence, puis on réessaie une fois
          try { await fetch(`${API_URL}/api/auth/refresh`, { method: "POST", credentials: "include" }); } catch { /* ignoré */ }
          res = await post("session", { takeover });
        }
        if (res.status === 403) { setStatus("denied"); return; }
        if (res.status === 409) { setStatus("locked"); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const conv = await Conversation.startSession({
          signedUrl: data.signed_url,
          dynamicVariables: { user_name: data.user_name || "Joël", salutation: data.salutation || `Bonjour ${data.user_name || "Joël"}.`, premiere_ouverture: data.premiere_ouverture || "oui" },
          clientTools: {
            aller_a_la_page: ({ page }: { page: string }) => {
              const route = PAGES[page];
              if (!route) return "page inconnue";
              navigate(route);
              return "ok";
            },
            defiler_la_page: ({ sens }: { sens: string }) => {
              window.scrollBy({ top: (sens === "haut" ? -1 : 1) * window.innerHeight * 0.8, behavior: "smooth" });
              return "ok";
            },
            fermer_la_fenetre: () => {
              const open = document.querySelector('[role="dialog"], [role="alertdialog"], [data-state="open"][data-radix-popper-content-wrapper]');
              if (!open) return "aucune fenêtre ouverte";
              const btn = open.querySelector<HTMLElement>('button[aria-label*="lose" i], button[aria-label*="ermer" i], [data-testid*="close" i]');
              if (btn) btn.click();
              else document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", keyCode: 27, bubbles: true }));
              return "ok";
            },
            revenir_en_arriere: () => {
              navigate(-1);
              return "ok";
            },
            arreter_la_voix: () => {
              window.setTimeout(() => { void stop(true); }, 2500);
              return "ok";
            },
          },
          onModeChange: ({ mode: m }: { mode: string }) => setMode(m === "speaking" ? "speaking" : "listening"),
          onDisconnect: () => {
            if (convRef.current) { convRef.current = null; clearTimer(); setStatus("idle"); }
          },
          onError: () => setStatus("error"),
        });
        convRef.current = conv;
        setStatus("live");
        clearTimer();
        timer.current = window.setInterval(async () => {
          try {
            const j = await (await post("heartbeat")).json();
            if (!j.active) { void stop(false); setStatus("locked"); }
          } catch { /* réseau instable */ }
        }, HEARTBEAT_MS);
      } catch {
        setStatus("error");
      } finally {
        busy.current = false;
      }
    },
    [navigate, post, stop]
  );

  // Contexte d'écran : Grafton sait quelle page vous regardez et peut vous poser une question dessus (envoyé seulement pendant une session vocale)
  useEffect(() => {
    if (status !== "live") return;
    const send = () => {
      const main = (document.querySelector("main") as HTMLElement | null) || document.body;
      const titres = Array.from(main.querySelectorAll("h1, h2")).slice(0, 6).map((h) => (h.textContent || "").trim()).filter(Boolean).join(" | ");
      const texte = (main.innerText || "").replace(/\s+/g, " ").trim().slice(0, 1500);
      try { convRef.current?.sendContextualUpdate(`ÉCRAN : page ${pathname}. Titres : ${titres || "aucun"}. Texte visible : ${texte}`); } catch { /* session fermée */ }
    };
    const t = window.setTimeout(send, 1200);
    return () => window.clearTimeout(t);
  }, [status, pathname]);

  // Niveau du micro : montre si Grafton vous entend ; alerte si rien n'arrive pendant 12 s
  useEffect(() => {
    if (status !== "live") { setLevel(0); setDeaf(false); return; }
    let heard = false;
    const t0 = Date.now();
    const id = window.setInterval(() => {
      const v = Number(convRef.current?.getInputVolume?.() ?? 0);
      setLevel(v);
      if (v > 0.02) { heard = true; setDeaf(false); }
      else if (!heard && Date.now() - t0 > 12000) setDeaf(true);
    }, 150);
    return () => window.clearInterval(id);
  }, [status]);

  // Démarrage automatique au premier geste (le navigateur interdit le micro sans geste utilisateur)
  useEffect(() => {
    if (!enabled || !readPref() || convRef.current) return;
    const go = () => { void start(false); };
    window.addEventListener("pointerdown", go, { once: true });
    window.addEventListener("keydown", go, { once: true });
    return () => {
      window.removeEventListener("pointerdown", go);
      window.removeEventListener("keydown", go);
    };
  }, [enabled, start, status]);

  // Fin de session si on quitte les pages concernées ou si l'utilisateur se déconnecte
  useEffect(() => {
    if (!enabled && convRef.current) void stop(false);
  }, [enabled, stop]);

  useEffect(() => {
    const release = () => {
      if (!convRef.current) return;
      navigator.sendBeacon(`${API_URL}/api/voice/release`, new Blob([JSON.stringify({ device_id: deviceId.current })], { type: "application/json" }));
    };
    window.addEventListener("pagehide", release);
    return () => { window.removeEventListener("pagehide", release); clearTimer(); void convRef.current?.endSession().catch(() => undefined); };
  }, []);

  if (!enabled || status === "denied") return null;

  const base = "fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-border/60 bg-background/95 px-3 py-2 text-xs shadow-lg backdrop-blur";
  if (status === "live")
    return (
      <div className={base} data-testid="voice-pill">
        <span className={`h-2.5 w-2.5 rounded-full ${mode === "speaking" ? "animate-pulse bg-emerald-500" : "bg-sky-500"}`} />
        <span>{mode === "speaking" ? "Grafton parle" : "Grafton écoute"}</span>
        <span className="inline-block h-1.5 w-10 overflow-hidden rounded-full bg-muted" title="Niveau du micro"><span className="block h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, Math.round(level * 400))}%` }} /></span>
        {deaf && <span className="text-destructive">Micro muet ? Vérifiez l'autorisation du micro</span>}
        <button onClick={() => stop(true)} className="ml-1 flex items-center gap-1 rounded-full bg-muted px-2 py-1 hover:bg-destructive/20" data-testid="voice-stop">
          <MicOff className="h-3.5 w-3.5" /> Couper
        </button>
      </div>
    );
  if (status === "starting")
    return <div className={base}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Connexion de la voix…</div>;
  if (status === "locked")
    return (
      <button className={base} onClick={() => start(true)}>
        <MonitorSmartphone className="h-3.5 w-3.5 text-amber-500" /> Voix active ailleurs : reprendre ici
      </button>
    );
  if (status === "error")
    return <button className={base} onClick={() => start(false)}><MicOff className="h-3.5 w-3.5" /> Voix indisponible : réessayer, ou utilisez le chat</button>;
  return (
    <button className={base} onClick={() => start(false)} data-testid="voice-start">
      <Mic className="h-3.5 w-3.5 text-primary" /> {status === "off" ? "Réactiver la voix" : "Activer la voix"}
    </button>
  );
}
